import { useContext } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { DiscoveryContext } from "../../luminaire/DiscoveryV2/discovery-context";
import { getProductDimensionsAvg } from "../../utils/brandIndexCalc";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

function scoreColor(score) {
  if (score == null) return "#8a94a6";
  if (score >= 9) return "#198754";
  if (score >= 7) return "#2196F3";
  return "#eca336";
}

/**
 * Shows product dimension scores per taxonomy category for a single product.
 * Reads directly from DiscoveryContext.taxonomies.
 */
export default function ProductDimensionsTable({ brandProduct, taxonomiesData }) {
  const discoveryCtx = useContext(DiscoveryContext);
  const taxonomies = taxonomiesData ?? discoveryCtx.taxonomies;

  if (!taxonomies?.data?.products?.length || !taxonomies?.data?.categories?.length) {
    return (
      <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
        Product Dimensions data not yet available.
      </Typography>
    );
  }

  const product = taxonomies.data.products.find(
    (p) => (p.name || "").toLowerCase().trim() === (brandProduct || "").toLowerCase().trim()
  );

  if (!product?.scores) {
    return (
      <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
        Product Dimensions data not available for this product.
      </Typography>
    );
  }

  const rows = taxonomies.data.categories.map((cat) => {
    const subcategories = Object.values(product.scores?.[cat.name]?.subcategories || {});
    const avg = subcategories.length
      ? subcategories.reduce((acc, sub) => acc + (isNaN(+sub.score) ? 0 : +sub.score), 0) / subcategories.length
      : null;
    const explanation = subcategories
      .map((sub) => sub.explanation)
      .filter(Boolean)
      .join(" • ");
    return { name: cat.name, score: avg, explanation };
  });

  const validScores = rows.map((r) => r.score).filter((s) => s != null);
  const overallAvg = validScores.length
    ? validScores.reduce((a, b) => a + b, 0) / validScores.length
    : null;

  return (
    <TableContainer>
      <Table sx={{ width: "100%", tableLayout: "fixed" }} size="medium">
        <colgroup>
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "60%" }} />
        </colgroup>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Category</StyledTableCell>
            <StyledTableCell align="center">Avg Score (0–10)</StyledTableCell>
            <StyledTableCell>Explanation</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <StyledTableRow key={row.name}>
              <StyledTableCell component="th" scope="row">{row.name}</StyledTableCell>
              <StyledTableCell align="center">
                <span style={{ color: scoreColor(row.score), fontWeight: 600 }}>
                  {row.score != null ? row.score.toFixed(1) : "—"}
                </span>
              </StyledTableCell>
              <StyledTableCell sx={{ wordBreak: "break-word" }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>
                  {row.explanation || "—"}
                </Typography>
              </StyledTableCell>
            </StyledTableRow>
          ))}
          <TableRow sx={{ backgroundColor: "#f0f4f8" }}>
            <TableCell sx={{ fontWeight: 700 }}>Overall Average</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, color: scoreColor(overallAvg) }}>
              {overallAvg != null ? overallAvg.toFixed(2) : "—"}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
